import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import VehiclePage, { vehicleHead, FLEET_LINKS, WHICH_VEHICLE, type VehicleConfig } from "@/components/VehiclePage";
import hero from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import mid from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";
import g1 from "@/assets/sprinter-limo-1.jpg?format=webp&quality=72&w=1000&imagetools";
import g2 from "@/assets/sprinter-exec-amber.jpg?format=webp&quality=72&w=1200&imagetools";
import g3 from "@/assets/sprinter-exec-yellow-cabin.jpg?format=webp&quality=72&w=1000&imagetools";
import corporateCard from "@/assets/aviation/atlanta-skyline.jpg?format=webp&quality=70&w=800&imagetools";

const cfg: VehicleConfig = {
  path: "/executive-sprinter-atlanta",
  title: "Executive Sprinter Atlanta | Chauffeured, Up to 13 Guests",
  description:
    "Chauffeured Executive Sprinter in Atlanta: conference seating for up to 13, two flat-screen TVs, WiFi and power at every seat. For corporate groups and client pickups. Call (678) 431-7222.",
  serviceType: "Executive Style Sprinter",
  eyebrow: "Our Fleet • Executive Sprinter",
  h1: "Executive Sprinter in Atlanta",
  intro:
    "You're bringing a team or welcoming a client. I'll help the travel feel considered: captain seating for up to 13, a quiet cabin, two flat-screen TVs and power at every seat.",
  heroImage: hero,
  heroAlt: "Executive Sprinter with conference table and green ambient lighting in Atlanta",
  quickFacts: [
    { value: "Up to 13", label: "Guests in captain seating" },
    { value: "2 TVs", label: "Flat screens, plus WiFi" },
    { value: "Power", label: "Outlets at every seat" },
    { value: "From $250", label: "Per hour, custom quotes" },
  ],
  introImage: g2,
  introImageAlt: "Executive Sprinter conference table with amber lighting and a flat-screen TV",
  midImage: mid,
  midAlt: "Executive Sprinter premium leather seating",
  aboutTitle: "Room to Prepare, Together",
  about: [
        "Keep your team in one quiet cabin, with conference-style seating for conversation or preparation before you arrive.",
        "We arrange board meetings, roadshows, conferences, client entertainment and group airport arrivals across Atlanta.",
      ],
  featuresTitle: "Executive Sprinter Features",
  features: [
    "Up to 13 passengers",
    "Conference-style captain seating",
    "Two flat-screen TVs",
    "Power outlets at every seat",
    "High-speed WiFi",
    "Refreshment bar with a built-in cooler",
  ],
  bookingBands: [
    { title: "Check the Executive Sprinter for Your Date", body: "Send the date, pickups and team size. We reply with a written quote." },
    { title: "Reserve the Executive Sprinter", body: "Sprinters start from $250/hour. Every quote is custom and in writing before you book." },
  ],
  bestFor: ["Corporate groups", "Client pickups", "Roadshows and site visits", "Conference transfers", "Airport group arrivals", "Wedding party transfers"],
  details: [
    {
      title: "A Cabin Built for Work on the Move",
      body: [
        "Conference-style seating faces the team toward each other, so a ride between meetings becomes part of the meeting. WiFi and power at every seat keep laptops and phones running all day.",
        "Book by the hour and one chauffeur stays with the group from the first pickup to the last drop-off. Companies that book regularly can open a corporate account for simpler billing and booking.",
      ],
      list: [
        "Airport group arrivals at Hartsfield-Jackson and private terminals",
        "Multi-stop days: office, client sites, lunch and dinner",
        "Conference and convention transfers downtown",
        "Client entertainment, from dinner to the game",
      ],
      image: g3,
      imageAlt: "Executive Sprinter cabin with conference table and yellow ceiling lighting",
    },
    WHICH_VEHICLE,
  ],
  gallery: [
    { src: hero, alt: "Executive Sprinter conference table", caption: "Conference seating" },
    { src: g2, alt: "Executive Sprinter entertainment screen", caption: "Flat-screen TVs" },
    { src: g1, alt: "Executive Sprinter captain chairs", caption: "Premium captain chairs" },
    { src: g3, alt: "Executive Sprinter cabin with ceiling lighting", caption: "A quiet cabin built for focus" },
    { src: mid, alt: "Executive Sprinter premium leather seating", caption: "Premium leather seating" },
  ],
  faqImage: g1,
  faqImageAlt: "Executive Sprinter captain chairs with pink ambient lighting",
  faqs: [
    { q: "How many people fit in the Executive Sprinter?", a: "Up to 13 passengers in captain-style conference seating." },
    { q: "Can we work during the ride?", a: "Yes. The cabin has high-speed WiFi, power outlets at every seat and two flat-screen TVs." },
    { q: "Do you offer corporate accounts?", a: "Yes. Companies that book regularly can open a corporate account for simpler billing and booking." },
    { q: "How much does the Executive Sprinter cost?", a: "Sprinters start from $250/hour. Custom quotes are given upon request." },
    hourlyMinimumFaq({ transfers: true }),
  ],
  related: [FLEET_LINKS.jet, FLEET_LINKS.limo, FLEET_LINKS.suburban, { to: "/corporate-transportation-atlanta", title: "Corporate Transportation", desc: "Chauffeured travel for teams and executives.", image: corporateCard }],
  ctaTitle: "Text Me Your Executive Itinerary",
  ctaBody: "Send me your date, pickup, team size and schedule. I'll check the arrangements personally and send your quote in writing.",
};

export const Route = createFileRoute("/executive-sprinter-atlanta")({
  head: () => vehicleHead(cfg),
  component: ExecutiveSprinterPage,
});

function ExecutiveSprinterPage() {
  return <VehiclePage cfg={cfg} />;
}
